// Remembering the module a person is in (PUT /api/me/last-module), fire-and-forget.
//
// Serialised and coalesced (Review Focus 1): at most one PUT in flight, and while one is,
// only the NEWEST pending module is kept. Rapid switching therefore ends with the server
// holding the last module opened. Unordered fire-and-forget PUTs could land in any order,
// leaving the person landing next time in a module they had already left.
//
// A failure is logged and never shown or retried; the worst outcome is landing on the
// fallback next time (spec, Routing).
//
// `save.current()` is the in-memory copy, updated at once on every switch (spec, Routing):
// landing on "/" later in the same session reads it, never the stale `last_module` that
// GET /api/me returned at sign-in. It is what the person last OPENED, so a failed PUT does
// not move it back.

export function createLastModuleSaver(put, initial, warn = console.warn) {
  let saved = initial ?? null;   // what the server is believed to hold
  let latest = initial ?? null;  // what the person last opened (the in-memory copy)
  let inFlight = false;
  let pending = null;

  function send(id) {
    inFlight = true;
    // put() is called NOW, not on a later tick, so a save is on the wire the moment
    // the person switches; a synchronous throw is treated like a rejected request.
    let request;
    try { request = Promise.resolve(put(id)); } catch (err) { request = Promise.reject(err); }
    request
      .then(() => { saved = id; }, (err) => warn('[shell] could not remember the module', id, err))
      .finally(() => {
        inFlight = false;
        if (pending !== null && pending !== saved) {
          const next = pending;
          pending = null;
          send(next);
        } else {
          pending = null;
        }
      });
  }

  function save(id) {
    latest = id;
    if (inFlight) { pending = id; return; }
    if (id === saved) return;
    send(id);
  }
  save.current = () => latest;
  return save;
}
